// ProductInfo.js - Updated with better labels and info tooltips
import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";
import appStyle from "./ProductInfo.module.css";
import { FaInfoCircle } from "react-icons/fa";

const getToken = () => localStorage.getItem("token");
const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

// Tooltip component
const InfoTooltip = ({ text }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <span
      className={appStyle.infoTooltipWrapper}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <FaInfoCircle className={appStyle.infoIcon} />
      {showTooltip && <span className={appStyle.infoTooltip}>{text}</span>}
    </span>
  );
};

const ProductInfo = forwardRef(({
  kbId,
  tenantId,
  appName,
  region,
  isModal = false,
  onSaveSuccess = null,
  hideTitle = false,
  onDataFetched = null
}, ref) => {

  console.log("kbIdddddddd", kbId)
  const [productName, setProductName] = useState("");
  const [productSummary, setProductSummary] = useState("");
  const [originalData, setOriginalData] = useState({ productName: "", productSummary: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [hasChanges, setHasChanges] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);

  // Helper function to parse API response
  const parseApiResponse = useCallback((responseData) => {
    if (responseData.body && typeof responseData.body === 'string') {
      try {
        return JSON.parse(responseData.body);
      } catch (parseError) {
        console.error('Error parsing response body:', parseError);
        return null;
      }
    }
    if (responseData.body && typeof responseData.body === 'object') {
      return responseData.body;
    }
    return responseData;
  }, []);

  // Fetch product metadata from API
  const fetchProductMetadata = useCallback(async () => {
    if (!kbId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const token = getToken();

      const response = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/ai_ingest_sys?task=product-metadata&kb_id=${kbId}`,
        {
          method: "GET",
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const parsedData = parseApiResponse(result);

        if (parsedData && parsedData.status === "success" && parsedData.data) {
          const name = parsedData.data.product_name || "";
          const summary = parsedData.data.product_summary || "";
          const hasContent = name.trim() !== "" || summary.trim() !== "";

          if (hasContent) {
            setProductName(name);
            setProductSummary(summary);
            setOriginalData({ productName: name, productSummary: summary });
            setHasExistingData(true);
            if (onDataFetched) {
              onDataFetched(true);
            }
          } else {
            setProductName("");
            setProductSummary("");
            setOriginalData({ productName: "", productSummary: "" });
            setHasExistingData(false);
            if (onDataFetched) {
              onDataFetched(false);
            }
          }
        } else {
          setProductName("");
          setProductSummary("");
          setOriginalData({ productName: "", productSummary: "" });
          setHasExistingData(false);
          if (onDataFetched) {
            onDataFetched(false);
          }
        }
      } else if (response.status === 404) {
        setProductName("");
        setProductSummary("");
        setOriginalData({ productName: "", productSummary: "" });
        setHasExistingData(false);
        if (onDataFetched) {
          onDataFetched(false);
        }
      } else {
        console.error("Failed to fetch product metadata");
        setHasExistingData(false);
        if (onDataFetched) {
          onDataFetched(false);
        }
      }
    } catch (error) {
      console.error("Error fetching product metadata:", error);
      setHasExistingData(false);
      if (onDataFetched) {
        onDataFetched(false);
      }
    } finally {
      setLoading(false);
    }
  }, [kbId, onDataFetched, parseApiResponse]);

  // Expose refresh method to parent via ref
  useImperativeHandle(ref, () => ({
    refresh: fetchProductMetadata,
    hasData: () => hasExistingData,
    getLoading: () => loading
  }));

  // Save product metadata to API
  const saveProductMetadata = async () => {
    if (!productName.trim() && !productSummary.trim()) {
      setMessage({ type: "error", text: "Please provide at least Bot Name or Bot Context" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      return false;
    }

    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const token = getToken();

      const response = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/ai_ingest_sys?task=product-metadata`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": token,
          },
          body: JSON.stringify({
            kb_id: kbId,
            product_name: productName.trim(),
            product_summary: productSummary.trim(),
          }),
        }
      );

      const result = await response.json();
      const parsedData = parseApiResponse(result);

      if (response.ok && parsedData && parsedData.status === "success") {
        setOriginalData({
          productName: productName.trim(),
          productSummary: productSummary.trim()
        });
        setHasChanges(false);
        setHasExistingData(true);
        setMessage({ type: "success", text: "✅ Product information saved successfully!" });
        setTimeout(() => setMessage({ type: "", text: "" }), 3000);

        if (onDataFetched) {
          onDataFetched(true);
        }

        if (onSaveSuccess) {
          onSaveSuccess();
        }
        return true;
      } else {
        const errorMsg = parsedData?.detail ||
          parsedData?.error ||
          parsedData?.message ||
          "Failed to save product information";
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error("Error saving product metadata:", error);
      setMessage({ type: "error", text: `❌ ${error.message || "Failed to save product information"}` });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Check for changes
  useEffect(() => {
    const nameChanged = productName.trim() !== originalData.productName;
    const summaryChanged = productSummary.trim() !== originalData.productSummary;
    setHasChanges(nameChanged || summaryChanged);
  }, [productName, productSummary, originalData]);

  // Fetch on mount and when kbId changes
  useEffect(() => {
    if (kbId) {
      fetchProductMetadata();
    } else {
      setLoading(false);
    }
  }, [kbId, fetchProductMetadata]);

  if (loading) {
    return (
      <div className={appStyle.productInfoSection}>
        {!hideTitle && <h3 className={appStyle.productInfoTitle}>📦 Product Information</h3>}
        <div className={appStyle.productInfoLoading}>Loading product information...</div>
      </div>
    );
  }

  return (
    <div className={appStyle.productInfoSection}>
      {!hideTitle && <h3 className={appStyle.productInfoTitle}>📦 Product Information</h3>}
      <p className={appStyle.productInfoDescription}>
        Provide information about your product to help the AI Agent understand your business better.
      </p>

      <div className={appStyle.productInfoForm}>
        <div className={appStyle.formGroup}>
          <label htmlFor={`productName${isModal ? '-modal' : ''}`} className={appStyle.formLabel}>
            Bot Name <span className={appStyle.optionalTag}>(Optional)</span>
            <InfoTooltip text="This is the name of your AI bot that will be displayed to users." />
          </label>
          <input
            id={`productName${isModal ? '-modal' : ''}`}
            type="text"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            placeholder="e.g., SupportBot, AssistantPro"
            className={appStyle.productNameInput}
            maxLength={100}
          />
          <small className={appStyle.formHint}>
            The name that will appear as your bot's display name
          </small>
        </div>

        <div className={appStyle.formGroup}>
          <label htmlFor={`productSummary${isModal ? '-modal' : ''}`} className={appStyle.formLabel}>
            Bot Context <span className={appStyle.optionalTag}>(Optional)</span>
            <InfoTooltip text="Provide context about what your bot does, its purpose, and what it can help users with." />
          </label>
          <textarea
            id={`productSummary${isModal ? '-modal' : ''}`}
            value={productSummary}
            onChange={(e) => setProductSummary(e.target.value)}
            placeholder="Describe what your bot does, its key features, and who it's for..."
            className={appStyle.productSummaryTextarea}
            rows={4}
            maxLength={500}
          />
          <small className={appStyle.formHint}>
            A brief description of your bot's purpose and capabilities (max 500 characters)
          </small>
          <div className={appStyle.characterCount}>
            {productSummary.length}/500 characters
          </div>
        </div>

        <div className={appStyle.productInfoActions}>
          <button
            onClick={saveProductMetadata}
            disabled={saving || !hasChanges}
            className={`${appStyle.saveProductButton} ${(!hasChanges || saving) ? appStyle.disabled : ""}`}
          >
            {saving ? "Saving..." : "💾 Save Product Info"}
          </button>
        </div>

        {message.text && (
          <div className={`${appStyle.productInfoMessage} ${appStyle[message.type]}`}>
            {message.text}
          </div>
        )}
      </div>

      {!isModal && <hr className={appStyle.productInfoDivider} />}
    </div>
  );
});

ProductInfo.displayName = 'ProductInfo';

export default ProductInfo;